// components/ui/BottomSheet.jsx
import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

export default function BottomSheet({
  open,
  onClose,
  title,
  children,
  closeOnOverlay = true,
  fullWidth = true, // sheet ocupa 100% del ancho
  maxWidthClass = 'max-w-5xl', // usado si fullWidth=false
  heightClass = 'h-[80vh]', // alto del sheet
  contentMaxWidthClass = 'max-w-[1240px]', // ancho máximo del contenido interno
  contentPaddingClass = 'px-4 sm:px-6', // padding horizontal del contenido
  withScrollShadows = true, // fades arriba/abajo del scroll
}) {
  const dialogId = useId();
  const titleId = `${dialogId}-title`;
  const closeBtnRef = useRef(null);
  const lastActiveRef = useRef(null);

  // ESC + bloqueo de scroll + retorno de foco
  useEffect(() => {
    if (!open) return;
    lastActiveRef.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    const id = requestAnimationFrame(() => closeBtnRef.current?.focus?.());

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      cancelAnimationFrame(id);
      lastActiveRef.current?.focus?.();
    };
  }, [open, onClose]);

  if (typeof window === 'undefined') return null;

  // ancho del sheet
  const widthClasses = fullWidth
    ? 'inset-x-0 w-screen max-w-none'
    : `left-0 right-0 w-full ${maxWidthClass} mx-auto`;

  return createPortal(
    <div
      aria-hidden={!open}
      className={`fixed inset-0 z-[9999] ${open ? 'pointer-events-auto' : 'pointer-events-none'}`}
    >
      {/* Overlay */}
      <button
        type="button"
        aria-label="Close overlay"
        onClick={closeOnOverlay ? onClose : undefined}
        className={`absolute inset-0 h-full w-full bg-black/40 backdrop-blur-[1px] transition-opacity duration-300 ease-out ${
          open ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* Sheet */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={[
          'absolute bottom-0 mx-auto',
          widthClasses,
          heightClass, // alto total del sheet
          'rounded-t-2xl bg-white shadow-2xl border border-slate-200',
          'transition-transform duration-300 ease-out will-change-transform',
          open ? 'translate-y-0' : 'translate-y-full',
          'dark:bg-slate-900 dark:border-slate-800',
          'flex flex-col', // estructura vertical
        ].join(' ')}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3">
          <span className="h-1.5 w-10 rounded-full bg-slate-300 dark:bg-slate-700" />
        </div>

        {/* Header (full-width) */}
        <div className="border-b border-slate-200 dark:border-slate-800">
          <div
            className={[
              'mx-auto w-full',
              contentMaxWidthClass,
              contentPaddingClass,
              'flex items-center justify-between py-3',
            ].join(' ')}
          >
            <h3 id={titleId} className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {title}
            </h3>
            <button
              ref={closeBtnRef}
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:hover:bg-slate-800"
            >
              <CloseIcon />
              <span className="sr-only">Close</span>
            </button>
          </div>
        </div>

        {/* Body scrolleable (centrado y con max-width 1240px) */}
        <div className="relative flex-1 overflow-y-auto overscroll-contain">
          {withScrollShadows && (
            <>
              <div className="pointer-events-none absolute inset-x-0 top-0 h-5 bg-gradient-to-b from-white to-transparent dark:from-slate-900" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-white to-transparent dark:from-slate-900" />
            </>
          )}
          <div
            className={[
              'mx-auto w-full',
              contentMaxWidthClass,
              contentPaddingClass,
              'pb-[env(safe-area-inset-bottom)]', // safe-area iOS
            ].join(' ')}
            style={{ contentVisibility: 'auto' }} // perf en listas largas
          >
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function CloseIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <path
        fillRule="evenodd"
        d="M10 8.586 5.293 3.879A1 1 0 1 0 3.879 5.293L8.586 10l-4.707 4.707a1 1 0 1 0 1.414 1.414L10 11.414l4.707 4.707a1 1 0 0 0 1.414-1.414L11.414 10l4.707-4.707A1 1 0 0 0 14.707 3.88L10 8.586Z"
        clipRule="evenodd"
      />
    </svg>
  );
}
